import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Images, Download, Loader2, Film, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PageHeader, Pager, Spinner, Empty } from "@/components/app/common";
import { MediaView } from "@/components/app/Checkout";
import { api, dt, mediaUrl, errMsg } from "@/lib/api";
import { StatusBadge } from "@/lib/labels";
import { cn } from "@/lib/utils";

const FILTERS = [["all", "Tümü"], ["images", "Görseller"], ["banner", "Banner"], ["video", "Video"], ["reels", "Reels"], ["video_requests", "Video Talepleri"], ["completed", "Başarılı"], ["failed", "Başarısız"]];
const WAITING = ["QUEUED", "PROCESSING", "PREPARING"];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState([]);
  const [merging, setMerging] = useState(false);
  const isReadyVideo = (g) => g.status === "COMPLETED" && g.output_type?.startsWith("video");
  const togglePick = (g) => setPicked((p) => (p.includes(g.id) ? p.filter((x) => x !== g.id) : p.length >= 10 ? (toast.error("En fazla 10 video seçebilirsiniz."), p) : [...p, g.id]));
  const merge = async () => {
    setMerging(true);
    try {
      await api.post("/generations/merge", { generation_ids: picked });
      toast.success("Videolar birleştiriliyor. Tek klip birkaç saniye içinde galerinizde olacak.");
      setSelecting(false); setPicked([]); setFilter("video"); setPage(1); load();
    } catch (e) { toast.error(errMsg(e)); }
    setMerging(false);
  };
  const load = useCallback(() => api.get("/generations", { params: { filter, page, limit: 24 } }).then(({ data }) => setRes(data)), [filter, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!res?.items.some((g) => WAITING.includes(g.status))) return;
    const t = setTimeout(load, res.items.some((g) => g.status !== "PREPARING" && WAITING.includes(g.status)) ? 5000 : 15000);
    return () => clearTimeout(t);
  }, [res, load]);

  return (
    <div data-testid="gallery-page">
      <PageHeader title="Galerim" desc="Oluşturduğunuz tüm içerikler burada saklanır.">
        {selecting ? (
          <>
            <Button variant="outline" className="rounded-xl" onClick={() => { setSelecting(false); setPicked([]); }} data-testid="merge-cancel-button"><X className="mr-2 h-4 w-4" />Vazgeç</Button>
            <Button className="btn-glow rounded-xl" disabled={picked.length < 2 || merging} onClick={merge} data-testid="merge-submit-button">
              {merging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Film className="mr-2 h-4 w-4" />}Tek Klip Yap ({picked.length})
            </Button>
          </>
        ) : (
          <Button variant="outline" className="rounded-xl" onClick={() => { setSelecting(true); if (!["video", "reels"].includes(filter)) { setFilter("video"); setPage(1); } }} data-testid="merge-mode-button"><Film className="mr-2 h-4 w-4" />Videoları Birleştir</Button>
        )}
      </PageHeader>
      {selecting && <p className="mb-4 rounded-xl border border-primary/30 bg-primary/10 p-3 text-sm" data-testid="merge-hint">Birleştirmek istediğiniz videoları sırayla seçin (2-10). Klip, seçim sırasına göre birleştirilir. Bu işlem kredi harcamaz.</p>}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => { setFilter(k); setPage(1); }} data-testid={`gallery-filter-${k}`}
            className={cn("whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors", filter === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50")}>{l}</button>
        ))}
      </div>
      {!res ? <Spinner /> : res.items.length === 0 ? (
        <Empty icon={Images} title="Burada henüz içerik yok" desc="Yeni bir içerik oluşturarak galerinizi doldurmaya başlayın."><Button asChild className="btn-glow rounded-xl"><Link to="/panel/olustur">İçerik Oluştur</Link></Button></Empty>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {res.items.map((g) => {
            const order = picked.indexOf(g.id);
            const pickable = selecting && isReadyVideo(g);
            return (
            <button key={g.id} onClick={() => (selecting ? pickable && togglePick(g) : setOpen(g))} disabled={selecting && !pickable}
              className={cn("glass-card glass-card-hover relative overflow-hidden text-left", selecting && !pickable && "opacity-40", order >= 0 && "ring-2 ring-primary")} data-testid={`gallery-item-${g.id}`}>
              {order >= 0 && <span className="absolute left-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground" data-testid={`merge-order-${g.id}`}>{order + 1}</span>}
              {pickable && order < 0 && <span className="absolute left-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-full border-2 border-white/80 bg-black/30"><Check className="h-3.5 w-3.5 text-white/0" /></span>}
              <div className="grid aspect-square place-items-center bg-muted/40">
                {g.output_file_id ? <MediaView id={g.output_file_id} type={g.output_type} controls={false} className="h-full w-full object-cover" />
                  : g.status === "PREPARING" ? <span className="px-4 text-center text-sm font-semibold" data-testid={`gallery-preparing-${g.id}`}>🔄 Videonuz hazırlanıyor...</span>
                  : ["QUEUED", "PROCESSING"].includes(g.status) ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <span className="px-4 text-center text-xs text-muted-foreground">{g.user_error}</span>}
              </div>
              <div className="space-y-1.5 p-3">
                <div className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold">{g.label}{g.duration ? ` · ${g.duration}sn` : ""}</span><StatusBadge status={g.status} /></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>{dt(g.created_at)}</span><span>{g.customer_credit_cost} kredi</span></div>
              </div>
            </button>
            );
          })}
        </div>
      )}
      {res && <Pager page={res.page} pages={res.pages} onChange={setPage} />}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="gallery-detail-dialog">
          {open && (
            <>
              <DialogHeader><DialogTitle className="font-display">{open.label}</DialogTitle></DialogHeader>
              {open.manual && open.status === "COMPLETED" && <div className="rounded-xl bg-emerald-500/15 p-3 text-center font-semibold text-emerald-500" data-testid="gallery-video-ready">✅ Videonuz hazırlandı</div>}
              {open.manual && open.status === "PREPARING" && <div className="rounded-xl bg-primary/10 p-6 text-center font-semibold" data-testid="gallery-video-preparing">🔄 Videonuz hazırlanıyor...</div>}
              {open.output_file_id && <MediaView id={open.output_file_id} type={open.output_type} className="max-h-[60vh] w-full rounded-xl bg-muted/30 object-contain" />}
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <div><div className="text-xs text-muted-foreground">Durum</div><StatusBadge status={open.status} /></div>
                <div><div className="text-xs text-muted-foreground">Tarih</div>{dt(open.created_at)}</div>
                <div><div className="text-xs text-muted-foreground">Kullanılan kredi</div>{["FAILED", "CANCELLED"].includes(open.status) ? `0 (${open.customer_credit_cost} iade edildi)` : open.customer_credit_cost}</div>
              </div>
              {open.status === "CANCELLED" && <p className="rounded-xl bg-rose-500/10 p-3 text-sm text-rose-500" data-testid="gallery-cancel-reason">{open.user_error}</p>}
              {open.video_request && (
                <div className="grid gap-2 rounded-xl border border-border p-4 text-sm sm:grid-cols-2" data-testid="gallery-video-request-info">
                  <div><span className="text-muted-foreground">Tür: </span>{open.video_request.category === "emlak" ? "Emlak" : "Otomobil"}</div>
                  <div><span className="text-muted-foreground">Başlık: </span>{open.video_request.title}</div>
                  {open.video_request.price && <div><span className="text-muted-foreground">Fiyat: </span>{open.video_request.price}</div>}
                  {open.video_request.location && <div><span className="text-muted-foreground">Konum: </span>{open.video_request.location}</div>}
                </div>
              )}
              <div className="text-sm"><div className="text-xs text-muted-foreground">{open.manual ? "Ek açıklama / istek" : "Prompt"}</div><p className="mt-1 whitespace-pre-line" data-testid="gallery-detail-prompt">{open.prompt || (open.manual ? "—" : "— (yalnızca görsel ile üretildi)")}</p></div>
              {open.output_file_id && <Button asChild variant="outline" className="rounded-xl" data-testid="gallery-download-button"><a href={mediaUrl(open.output_file_id)} download target="_blank" rel="noreferrer"><Download className="mr-2 h-4 w-4" />{open.kind === "video" ? "Videoyu İndir" : "İndir"}</a></Button>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
